import { openDB, type DBSchema, type IDBPDatabase } from "idb";

// ── Sync status for all cached records ──────────────────────────
export type SyncStatus = "synced" | "pending" | "conflict";

// ── Sync Queue Entry ────────────────────────────────────────────
export interface SyncQueueEntry {
  id: string;
  entity_type: string;
  entity_id: string;
  operation: "create" | "update" | "delete";
  payload_json: any;
  created_at: string;
  retry_count: number;
  last_error: string | null;
  status: "queued" | "syncing" | "failed" | "synced";
}

// ── Meta store ──────────────────────────────────────────────────
export interface MetaEntry {
  key: string;
  value: any;
}

// ── DB Schema ───────────────────────────────────────────────────
interface SmrtlyFitDB extends DBSchema {
  profiles: {
    key: string;
    value: any & { _syncStatus: SyncStatus };
  };
  workout_templates: {
    key: string;
    value: any & { _syncStatus: SyncStatus };
    indexes: { "by-user": string };
  };
  template_exercises: {
    key: string;
    value: any & { _syncStatus: SyncStatus };
    indexes: { "by-template": string };
  };
  workout_sessions: {
    key: string;
    value: any & { _syncStatus: SyncStatus };
    indexes: { "by-user": string };
  };
  session_exercises: {
    key: string;
    value: any & { _syncStatus: SyncStatus };
    indexes: { "by-session": string };
  };
  session_sets: {
    key: string;
    value: any & { _syncStatus: SyncStatus };
    indexes: { "by-exercise": string };
  };
  food_logs: {
    key: string;
    value: any & { _syncStatus: SyncStatus };
    indexes: { "by-user-date": [string, string] };
  };
  body_metrics: {
    key: string;
    value: any & { _syncStatus: SyncStatus };
    indexes: { "by-user": string };
  };
  check_ins: {
    key: string;
    value: any & { _syncStatus: SyncStatus };
    indexes: { "by-user": string };
  };
  exercise_catalog: {
    key: string;
    value: any;
  };
  custom_foods: {
    key: string;
    value: any & { _syncStatus: SyncStatus };
    indexes: { "by-user": string };
  };
  macro_targets: {
    key: string;
    value: any & { _syncStatus: SyncStatus };
    indexes: { "by-user": string };
  };
  macro_calculations: {
    key: string;
    value: any & { _syncStatus: SyncStatus };
    indexes: { "by-user": string };
  };
  orm_calculations: {
    key: string;
    value: any & { _syncStatus: SyncStatus };
    indexes: { "by-user": string };
  };
  water_logs: {
    key: string;
    value: any & { _syncStatus: SyncStatus };
    indexes: { "by-user-date": [string, string] };
  };
  steps_entries: {
    key: string;
    value: any & { _syncStatus: SyncStatus };
    indexes: { "by-user-date": [string, string] };
  };
  meal_templates: {
    key: string;
    value: any & { _syncStatus: SyncStatus };
    indexes: { "by-user": string };
  };
  meal_template_items: {
    key: string;
    value: any & { _syncStatus: SyncStatus };
    indexes: { "by-template": string };
  };
  gym_passes: {
    key: string;
    value: any & { _syncStatus: SyncStatus };
    indexes: { "by-user": string };
  };
  sync_queue: {
    key: string;
    value: SyncQueueEntry;
    indexes: { "by-status": string };
  };
  meta: {
    key: string;
    value: MetaEntry;
  };
}

const DB_NAME = "smrtlyfit-offline";
const DB_VERSION = 5;

let dbPromise: Promise<IDBPDatabase<SmrtlyFitDB>> | null = null;

export function getDb(): Promise<IDBPDatabase<SmrtlyFitDB>> {
  if (!dbPromise) {
    dbPromise = openDB<SmrtlyFitDB>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          // Profiles
          db.createObjectStore("profiles", { keyPath: "user_id" });

          // Workout templates
          const wtStore = db.createObjectStore("workout_templates", { keyPath: "id" });
          wtStore.createIndex("by-user", "user_id");

          // Template exercises
          const teStore = db.createObjectStore("template_exercises", { keyPath: "id" });
          teStore.createIndex("by-template", "template_id");

          // Workout sessions
          const wsStore = db.createObjectStore("workout_sessions", { keyPath: "id" });
          wsStore.createIndex("by-user", "user_id");

          // Session exercises
          const seStore = db.createObjectStore("session_exercises", { keyPath: "id" });
          seStore.createIndex("by-session", "session_id");

          // Session sets
          const ssStore = db.createObjectStore("session_sets", { keyPath: "id" });
          ssStore.createIndex("by-exercise", "session_exercise_id");

          // Food logs
          const flStore = db.createObjectStore("food_logs", { keyPath: "id" });
          flStore.createIndex("by-user-date", ["user_id", "logged_date"]);

          // Body metrics
          const bmStore = db.createObjectStore("body_metrics", { keyPath: "id" });
          bmStore.createIndex("by-user", "user_id");

          // Check-ins
          const ciStore = db.createObjectStore("check_ins", { keyPath: "id" });
          ciStore.createIndex("by-user", "user_id");

          // Exercise catalog
          db.createObjectStore("exercise_catalog", { keyPath: "id" });

          // Custom foods
          const cfStore = db.createObjectStore("custom_foods", { keyPath: "id" });
          cfStore.createIndex("by-user", "user_id");

          // Macro targets
          const mtStore = db.createObjectStore("macro_targets", { keyPath: "id" });
          mtStore.createIndex("by-user", "user_id");

          // Macro calculations
          const mcStore = db.createObjectStore("macro_calculations", { keyPath: "id" });
          mcStore.createIndex("by-user", "user_id");

          // ORM calculations
          const ormStore = db.createObjectStore("orm_calculations", { keyPath: "id" });
          ormStore.createIndex("by-user", "user_id");

          // Sync queue
          const sqStore = db.createObjectStore("sync_queue", { keyPath: "id" });
          sqStore.createIndex("by-status", "status");

          // Meta
          db.createObjectStore("meta", { keyPath: "key" });
        }

        if (oldVersion < 2) {
          if (!db.objectStoreNames.contains("water_logs")) {
            const wlStore = db.createObjectStore("water_logs", { keyPath: "id" });
            wlStore.createIndex("by-user-date", ["user_id", "logged_date"]);
          }
        }

        if (oldVersion < 3) {
          if (!db.objectStoreNames.contains("steps_entries")) {
            const seStore = db.createObjectStore("steps_entries", { keyPath: "id" });
            seStore.createIndex("by-user-date", ["user_id", "date_local"]);
          }
        }

        if (oldVersion < 4) {
          if (!db.objectStoreNames.contains("meal_templates")) {
            const mtStore = db.createObjectStore("meal_templates", { keyPath: "id" });
            mtStore.createIndex("by-user", "user_id");
          }
          if (!db.objectStoreNames.contains("meal_template_items")) {
            const mtiStore = db.createObjectStore("meal_template_items", { keyPath: "id" });
            mtiStore.createIndex("by-template", "template_id");
          }
        }

        if (oldVersion < 5) {
          if (!db.objectStoreNames.contains("gym_passes")) {
            const gpStore = db.createObjectStore("gym_passes", { keyPath: "id" });
            gpStore.createIndex("by-user", "user_id");
          }
        }
      },
    });
  }
  return dbPromise;
}

// ── Helpers ─────────────────────────────────────────────────────

export async function getMeta(key: string): Promise<any> {
  const db = await getDb();
  const entry = await db.get("meta", key);
  return entry?.value ?? null;
}

export async function setMeta(key: string, value: any): Promise<void> {
  const db = await getDb();
  await db.put("meta", { key, value });
}

export async function clearAllOfflineData(): Promise<void> {
  const db = await getDb();
  const storeNames = [
    "profiles", "workout_templates", "template_exercises",
    "workout_sessions", "session_exercises", "session_sets",
    "food_logs", "body_metrics", "check_ins", "exercise_catalog",
    "custom_foods", "macro_targets", "macro_calculations",
    "orm_calculations", "water_logs", "steps_entries", "meal_templates", "meal_template_items", "gym_passes", "sync_queue", "meta",
  ] as const;
  const tx = db.transaction([...storeNames], "readwrite");
  await Promise.all(storeNames.map((name) => tx.objectStore(name).clear()));
  await tx.done;
}

export async function estimateStorageUsage(): Promise<{ used: number; quota: number }> {
  if (navigator.storage && navigator.storage.estimate) {
    const est = await navigator.storage.estimate();
    return { used: est.usage ?? 0, quota: est.quota ?? 0 };
  }
  return { used: 0, quota: 0 };
}
